// Prefix for the localForage key under which the rolling list of admin page
// views is stored. The current admin's canonical id is appended (see
// `buildAdminRecentlyViewedStorageKey`) so history is scoped per identity and
// doesn't bleed across logout / account-switch / impersonation on a shared
// browser profile.
export const ADMIN_RECENTLY_VIEWED_STORAGE_KEY_PREFIX = 'admin-recently-viewed'

// Build the identity-scoped storage key for the current admin. Falls back to an
// `anonymous` bucket before an identity resolves (e.g. first render pre-auth);
// those events never mix with a signed-in admin's history.
export const buildAdminRecentlyViewedStorageKey = (identityId?: string): string =>
  `${ADMIN_RECENTLY_VIEWED_STORAGE_KEY_PREFIX}:${identityId ?? 'anonymous'}`

// Only views within this trailing window count toward the "Recently viewed" list.
export const RECENTLY_VIEWED_WINDOW_MS = 14 * 24 * 60 * 60 * 1000 // 2 weeks

// Hard cap on persisted view events to bound localForage growth. Events are
// pruned by age first; this cap is a backstop for very high-traffic sessions.
export const RECENTLY_VIEWED_MAX_EVENTS = 500

// Number of pages surfaced on the admin homepage.
export const RECENTLY_VIEWED_DISPLAY_COUNT = 6

// A single recorded page view.
//
// `key` is the normalized route pattern (dynamic ids collapsed) used to
// aggregate repeat visits to the same logical page. `path` is the concrete URL
// of this specific visit, kept so the rendered link points at a real
// destination (e.g. the last `/app/admin/sites/123` rather than the `:id`
// pattern).
export interface AdminPageView {
  key: string
  path: string
  routeName?: string
  viewedAt: number
}

// An aggregated entry rendered in the list.
export interface RecentlyViewedEntry {
  key: string
  path: string
  routeName?: string
  count: number
  lastViewedAt: number
}
